import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, AlertTriangle, AlertCircle, Info, Bug } from "lucide-react";
import { adminApi, type AuditLogRow, type QueryAuditLogsInput } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { downloadCsv } from "@/lib/csv";

export const Route = createFileRoute("/_authed/audit")({
  component: AuditPage,
});

const SEVERITIES = ["", "info", "warning", "error", "critical"] as const;
const ACTOR_TYPES = ["", "platform_admin", "end_user", "service_account", "system"] as const;

function AuditPage() {
  const [eventType, setEventType] = useState("");
  const [severity, setSeverity] = useState<(typeof SEVERITIES)[number]>("");
  const [actorType, setActorType] = useState<(typeof ACTOR_TYPES)[number]>("");
  const [userId, setUserId] = useState("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedUserId = useDebouncedValue(userId);
  const [selected, setSelected] = useState<AuditLogRow | null>(null);

  const filters: QueryAuditLogsInput = useMemo(
    () => ({
      eventType: eventType || undefined,
      severity: severity || undefined,
      actorType: actorType || undefined,
      userId: debouncedUserId || undefined,
      limit: 50,
    }),
    [eventType, severity, actorType, debouncedUserId],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.audit.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.queryAuditLogs()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.logs) ?? [];

  function exportCsv() {
    downloadCsv(
      `audit-logs-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      rows.map((r) => ({
        timestamp: new Date(r.timestamp).toISOString(),
        eventType: r.eventType,
        severity: r.severity,
        actorType: r.actorType,
        userId: r.userId ?? "",
        action: r.action ?? "",
        resource: r.resource ?? "",
        result: r.result,
        ipAddress: r.ipAddress ?? "",
        errorMessage: r.errorMessage ?? "",
        metadata: JSON.stringify(r.metadata ?? {}),
      })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
          <p className="text-sm text-muted-foreground">
            Every admin action and security event. {rows.length.toLocaleString()} loaded.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Event type</label>
            <Input
              placeholder="e.g. admin.action"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Severity</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as (typeof SEVERITIES)[number])}
            >
              {SEVERITIES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All severities"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Actor type</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={actorType}
              onChange={(e) => setActorType(e.target.value as (typeof ACTOR_TYPES)[number])}
            >
              {ACTOR_TYPES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All actors"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">User ID</label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-2 font-medium">Time</th>
                <th className="px-4 py-2 font-medium">Sev</th>
                <th className="px-4 py-2 font-medium">Event</th>
                <th className="px-4 py-2 font-medium">Actor</th>
                <th className="px-4 py-2 font-medium">User / target</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No matching entries.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b hover:bg-muted/50"
                    onClick={() => setSelected(r)}
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
                      {format(new Date(r.timestamp), "yyyy-MM-dd HH:mm:ss")}
                    </td>
                    <td className="px-4 py-2">
                      <SeverityBadge severity={r.severity} />
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{r.eventType}</td>
                    <td className="px-4 py-2 text-xs">{r.actorType}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.userId ?? "—"}</td>
                    <td className="px-4 py-2 text-xs">{r.action ?? "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs",
                          r.result === "success"
                            ? "bg-success/10 text-success"
                            : "bg-destructive/10 text-destructive",
                        )}
                      >
                        {r.result}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t p-3 text-xs">
          <div className="text-muted-foreground">
            {rows.length.toLocaleString()} rows{query.hasNextPage ? " (more available)" : ""}
          </div>
          {query.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      </Card>

      {selected ? (
        <DetailDrawer entry={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: AuditLogRow["severity"] }) {
  const map: Record<string, { icon: typeof Info; className: string }> = {
    info: { icon: Info, className: "bg-muted text-muted-foreground" },
    warning: { icon: AlertTriangle, className: "bg-warning/15 text-warning" },
    error: { icon: AlertCircle, className: "bg-destructive/10 text-destructive" },
    critical: { icon: Bug, className: "bg-destructive text-destructive-foreground" },
  };
  const m = map[severity] ?? map.info;
  const Icon = m.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
        m.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {severity}
    </span>
  );
}

function DetailDrawer({ entry, onClose }: { entry: AuditLogRow; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-background/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-lg overflow-y-auto border-l bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-mono text-muted-foreground">{entry.id}</div>
            <h2 className="text-lg font-semibold">{entry.eventType}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="mt-4 space-y-3 text-sm">
          <Row label="Time">{format(new Date(entry.timestamp), "yyyy-MM-dd HH:mm:ss.SSS")}</Row>
          <Row label="Severity">{entry.severity}</Row>
          <Row label="Actor">{entry.actorType}</Row>
          <Row label="User ID">{entry.userId ?? "—"}</Row>
          <Row label="Action">{entry.action ?? "—"}</Row>
          <Row label="Resource">{entry.resource ?? "—"}</Row>
          <Row label="Result">{entry.result}</Row>
          <Row label="IP">{entry.ipAddress ?? "—"}</Row>
          {entry.errorMessage ? <Row label="Error">{entry.errorMessage}</Row> : null}
          <div>
            <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">Metadata</div>
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(entry.metadata ?? {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs font-medium uppercase text-muted-foreground">{label}</span>
      <span className="text-right font-mono text-xs">{children}</span>
    </div>
  );
}
